import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { gouvernanceData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import { Users, Scale, Shield, FileText, ArrowRight, Download } from 'lucide-react';

const GouvernancePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = gouvernanceData[language] || gouvernanceData.fr;

  const iconMap = {
    Users: Users,
    Scale: Scale,
    Shield: Shield,
    FileText: FileText
  };

  const sectionColors = [
    { bg: isDark ? 'bg-[#5B2D8E]/10' : 'bg-[#5B2D8E]/5', border: 'border-[#5B2D8E]', icon: 'text-[#5B2D8E]' },
    { bg: isDark ? 'bg-[#FFC107]/10' : 'bg-[#FFC107]/5', border: 'border-[#FFC107]', icon: 'text-[#B8860B]' },
    { bg: isDark ? 'bg-[#0D9488]/10' : 'bg-[#0D9488]/5', border: 'border-[#0D9488]', icon: 'text-[#0D9488]' },
    { bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50', border: 'border-blue-500', icon: 'text-blue-500' }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">Fonctionnement</span>
              <h1 className="heading-1 mb-4">{content.title}</h1>
              <div className="divider mx-auto mb-6" />
              <p className="body-large">{content.subtitle}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-8">
            {content.sections.map((section, index) => {
              const Icon = iconMap[section.icon] || Users;
              const colors = sectionColors[index % sectionColors.length];
              
              return (
                <AnimatedSection key={section.id} delay={index * 0.15}>
                  <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                    <div className={`p-6 ${colors.bg} border-l-4 ${colors.border}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-white/10' : 'bg-white'} flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 ${colors.icon}`} />
                        </div>
                        <h2 className="heading-3">{section.title}</h2>
                      </div>
                    </div>
                    <div className="p-6">
                      <ul className="space-y-3">
                        {section.content.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${colors.icon.replace('text-', 'bg-')}`} />
                            <span className="text-theme-secondary">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Banner */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <Shield className="w-16 h-16 mx-auto mb-6 text-white/80" />
              <h2 className="heading-2 text-white mb-4">Notre engagement</h2>
              <p className="text-xl text-white/80 mb-8" style={{ fontFamily: 'var(--font-serif)' }}>
                « La confiance se mérite. Elle se construit par la preuve, pas par les promesses. »
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-secondary" disabled>
                  <Download className="w-5 h-5" />
                  {content.cta.report}
                  <span className="text-xs opacity-60">(Bientôt)</span>
                </button>
                <Link to="/constitution" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors">
                  {content.cta.charter}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Link to Transparence */}
      <section className={`py-12 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <p className="text-theme-secondary mb-4">
                Pour plus de détails sur l'utilisation de vos données et nos finances :
              </p>
              <Link to="/transparence" className={`inline-flex items-center gap-2 font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'} hover:underline`}>
                Consulter notre page Transparence
                <ArrowRight className="w-4 h-4" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GouvernancePage;
